import { Button, Lock, LockScreen } from "@ews-admin/global-design-system";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { usePinLockTexts } from "../../../hooks/usePinLockTexts";
import { pinApi } from "../../../services/pin.api";
import { selectPinLockState } from "../../../store/reducers/sessionSlice";
import { pinErrorMessage } from "../../../utils/pin-utils";

interface ChangePinSectionProps {
  onChanged: () => void;
}

/** User settings: replaces the PIN of the lock screen (MED-1621). */
export const ChangePinSection: React.FC<ChangePinSectionProps> = ({
  onChanged,
}) => {
  const { t } = useTranslation();
  const { hasPin } = useSelector(selectPinLockState);
  const texts = usePinLockTexts();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // No PIN yet, or an API without the PIN lock: nothing to change.
  if (hasPin !== true) return null;

  const close = () => {
    setOpen(false);
    setError(null);
  };

  const onChangePin = async (currentPin: string, newPin: string) => {
    setError(null);
    try {
      await pinApi.change(currentPin, newPin);
    } catch (err) {
      // LockScreen expects the error text before the promise rejects.
      setError(pinErrorMessage(t, err));
      throw err;
    }
    close();
    onChanged();
  };

  return (
    <div className="pt-6 mt-8 space-y-4 border-t border-neutral-200" data-testid="change-pin-section">
      <div className="flex items-center space-x-3">
        <div className="flex justify-center items-center w-8 h-8 rounded-lg bg-ews-primary-100">
          <Lock className="w-4 h-4 text-primary-600" />
        </div>
        <h3 className="text-lg font-semibold text-neutral-900">
          {t("userSettings.pin.title")}
        </h3>
      </div>
      <p className="text-sm text-neutral-600">{t("userSettings.pin.description")}</p>
      <div className="flex justify-end">
        <Button type="button" variant="outline" onClick={() => setOpen(true)}>
          {t("userSettings.pin.changeButton")}
        </Button>
      </div>
      {open && (
        <LockScreen
          mode="change"
          texts={texts}
          error={error}
          onChangePin={onChangePin}
          onCancel={close}
        />
      )}
    </div>
  );
};
